import { useMemo, useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { SkyButton, SkyTooltip } from "@skypointcloud/skypoint-ui";
import { ArrowLeft, Layers, Bookmark, X, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { ROUTES } from "@/components/shared/constant";
import { useMsalAuth } from "@/hooks/useMsalAuth";
import {
  PORTAL_TAB_DEFINITIONS,
  DEFAULT_TAB,
  formatWorkTypeLabel,
} from "@/views/portals/shared/portalTabDefinitions";
import {
  useGetPortalQuery,
  useGetRequestTypesQuery,
  useGetWorkTypeSettingsQuery,
  useGetWorkItemCountsQuery,
  useGetSavedViewsQuery,
  useDeleteSavedViewMutation,
  useUpdatePortalMutation,
  type SavedView,
} from "@/store/api/portalsApi";
import { usePortalFilters } from "@/hooks/usePortalFilters";

const PORTAL_PATH_PATTERN = /^\/portals\/([^/]+)\//;

export function PortalWorkTypeSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getQueryString } = useMsalAuth();

  // Extract portalKey from pathname — this component renders outside <Route> so useParams is empty
  const portalKey = location.pathname.match(PORTAL_PATH_PATTERN)?.[1] || "";

  const { workType: activeWorkType, setWorkType } = usePortalFilters();

  const { data: portal } = useGetPortalQuery(portalKey, { skip: !portalKey });
  const { data: requestTypesData, isSuccess: rtLoaded } = useGetRequestTypesQuery(
    { portalKey: portalKey },
    { skip: !portalKey },
  );
  const { data: wtSettingsData } = useGetWorkTypeSettingsQuery(portalKey, { skip: !portalKey });
  const { data: countsData } = useGetWorkItemCountsQuery(portalKey, { skip: !portalKey });

  const color = portal?.color || "#0078D4";

  // Build work type list with counts from the dedicated counts endpoint
  const workTypes = useMemo(() => {
    const requestTypes = requestTypesData?.data ?? [];
    const wtSettings = wtSettingsData?.data ?? [];
    const byWorkType = countsData?.byWorkType ?? {};

    // Collect all work type names from all sources
    const allNames = new Set<string>();
    for (const wt of wtSettings) allNames.add(wt.workType);
    for (const rt of requestTypes) {
      if (rt.workType) allNames.add(rt.workType);
    }
    // Include work types that have counts even if not in settings
    for (const wt of Object.keys(byWorkType)) {
      allNames.add(wt);
    }

    return Array.from(allNames)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({ type, count: byWorkType[type] ?? 0 }));
  }, [requestTypesData, wtSettingsData, countsData]);

  const requestTypes = requestTypesData?.data ?? [];

  /** Resolve a work type slug to its request type IDs */
  const resolveIds = useCallback(
    (wt: string) =>
      requestTypes
        .filter((rt) => rt.workType?.toLowerCase() === wt.toLowerCase())
        .map((rt) => rt.id),
    [requestTypes],
  );

  const handleWorkTypeClick = useCallback(
    (wt: string) => {
      const ids = resolveIds(wt);
      setWorkType(wt, ids.length > 0 ? ids : undefined);
    },
    [resolveIds, setWorkType],
  );

  const handleAllItemsClick = useCallback(() => {
    setWorkType(undefined);
    // If on a non-list view, redirect to list
    const currentPath = location.pathname;
    const isNonListView =
      currentPath.includes("/board") ||
      currentPath.includes("/calendar") ||
      currentPath.includes("/queues");
    if (isNonListView && portalKey) {
      navigate(PORTAL_TAB_DEFINITIONS[DEFAULT_TAB].route.replace(":portalKey", portalKey), {
        replace: true,
      });
    }
  }, [setWorkType, location.pathname, portalKey, navigate]);

  // Auto-resolve requestTypeIds when request types load (handles direct URL navigation)
  useEffect(() => {
    if (!rtLoaded || !activeWorkType) return;
    const ids = resolveIds(activeWorkType);
    if (ids.length > 0) {
      setWorkType(activeWorkType, ids);
    }
  }, [rtLoaded]); // only run when request types first load

  // ── Saved Views ──────────────────────────────────────────
  const { data: savedViewsData } = useGetSavedViewsQuery(portalKey, { skip: !portalKey });
  const [deleteSavedView] = useDeleteSavedViewMutation();
  const [updatePortal] = useUpdatePortalMutation();
  const [, setSearchParams] = useSearchParams();
  const savedViews = savedViewsData?.data ?? [];

  // Track active view via URL param
  const activeViewId = new URLSearchParams(location.search).get("viewId");

  const applyView = useCallback(
    (view: SavedView) => {
      // Apply filters to URL params
      const params = new URLSearchParams();
      params.set("viewId", view.id);
      const f = view.filters;
      if (f.search) params.set("search", f.search);
      if (f.statusId) params.set("statusId", f.statusId);
      if (f.priority) params.set("priority", f.priority);
      if (f.assigneeId) params.set("assigneeId", f.assigneeId);
      if (f.workType) params.set("workType", f.workType);
      if (f.requestTypeIds?.length) params.set("requestTypeIds", f.requestTypeIds.join(","));
      if (f.customFieldFilters?.length)
        params.set("cfFilters", JSON.stringify(f.customFieldFilters));
      setSearchParams(params, { replace: true });

      // Apply saved columns to portal settings
      if (view.columns?.length && portalKey && portal) {
        const currentColumns = (portal.settings?.listViewColumns as string[]) ?? [];
        const columnsChanged = JSON.stringify(currentColumns) !== JSON.stringify(view.columns);
        if (columnsChanged) {
          updatePortal({
            portalKey,
            body: {
              settings: {
                ...portal.settings,
                listViewColumns: view.columns,
              },
            },
          });
        }
      }
    },
    [setSearchParams, portalKey, portal, updatePortal],
  );

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("portal-sidebar-collapsed") === "true",
  );
  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("portal-sidebar-collapsed", String(next));
  };

  const activeClass = collapsed
    ? "flex items-center justify-center p-2 cursor-pointer rounded-lg mb-0.5 text-[13px] font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 shadow-sm border-none w-full"
    : "flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg mb-0.5 text-[13px] font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 shadow-sm border-none w-full text-left";
  const inactiveClass = collapsed
    ? "flex items-center justify-center p-2 cursor-pointer rounded-lg mb-0.5 text-[13px] font-normal text-gray-600 dark:text-gray-300 bg-transparent border-none w-full transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
    : "flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg mb-0.5 text-[13px] font-normal text-gray-600 dark:text-gray-300 bg-transparent border-none w-full text-left transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100";

  const sidebarItem = (content: React.ReactElement, tooltip: string) =>
    collapsed ? (
      <SkyTooltip
        content={tooltip}
        side="right"
      >
        {content}
      </SkyTooltip>
    ) : (
      content
    );

  return (
    <div
      className={`relative ${collapsed ? "w-[52px]" : "w-[220px]"} h-full border-r border-gray-200 dark:border-gray-700 flex flex-col bg-gray-50 dark:bg-gray-800 transition-[width] duration-200`}
    >
      {/* Header: Back button — matches PortalTopBar h-12 */}
      <div
        className={`relative flex items-center h-12 shrink-0 ${collapsed ? "justify-center px-1" : "px-4"} border-b border-gray-200 dark:border-gray-700`}
      >
        {collapsed ? (
          <SkyTooltip
            content="Go back"
            side="right"
          >
            <button
              type="button"
              onClick={() => navigate(ROUTES.PORTALS + getQueryString())}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer border-none bg-transparent"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          </SkyTooltip>
        ) : (
          <SkyButton
            appearance="transparent"
            icon={ArrowLeft}
            size="small"
            onClick={() => navigate(ROUTES.PORTALS + getQueryString())}
          >
            Go back
          </SkyButton>
        )}

        {/* Edge toggle chevron — positioned at bottom-right corner of header */}
        <button
          type="button"
          onClick={toggleCollapse}
          className="absolute -bottom-3 -right-3 z-[950] flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm hover:text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Work type list */}
      <nav className="flex-1 overflow-y-auto p-2">
        {/* All Items */}
        {sidebarItem(
          <button
            className={activeWorkType === undefined ? activeClass : inactiveClass}
            onClick={handleAllItemsClick}
          >
            <Layers className="w-4 h-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="truncate">All Items</span>
                <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                  {countsData?.total ?? 0}
                </span>
              </>
            )}
          </button>,
          `All Items (${countsData?.total ?? 0})`,
        )}

        {workTypes.length > 0 && (
          <>
            {!collapsed && (
              <div className="mt-3 mb-0.5 px-3 py-1.5 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em]">
                Work Types
              </div>
            )}
            {collapsed && <div className="my-1 border-t border-gray-200 dark:border-gray-700" />}

            {workTypes.map(({ type, count }) => {
              const isActive = activeWorkType?.toLowerCase() === type.toLowerCase();
              const label = formatWorkTypeLabel(type);
              return sidebarItem(
                <button
                  key={type}
                  className={isActive ? activeClass : inactiveClass}
                  onClick={() => handleWorkTypeClick(type)}
                >
                  {collapsed ? (
                    <Tag className="w-4 h-4 shrink-0" />
                  ) : (
                    <>
                      <span className="truncate">{label}</span>
                      <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {count}
                      </span>
                    </>
                  )}
                </button>,
                `${label} (${count})`,
              );
            })}
          </>
        )}

        {/* Saved Views */}
        {savedViews.length > 0 && (
          <>
            {!collapsed && (
              <div className="mt-3 mb-0.5 px-3 py-1.5 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em]">
                Views
              </div>
            )}
            {collapsed && <div className="my-1 border-t border-gray-200 dark:border-gray-700" />}

            {savedViews.map((view) => {
              const isActive = activeViewId === view.id;
              return sidebarItem(
                <div
                  key={view.id}
                  className={`group ${isActive ? activeClass : inactiveClass}`}
                  onClick={() => applyView(view)}
                >
                  <Bookmark className="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
                  {!collapsed && (
                    <>
                      <span className="truncate flex-1">{view.name}</span>
                      <button
                        type="button"
                        className="hidden rounded p-0.5 text-gray-400 hover:text-red-500 group-hover:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSavedView({ portalKey, viewId: view.id });
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>,
                view.name,
              );
            })}
          </>
        )}
      </nav>
    </div>
  );
}
